import React, { useEffect, useState, useRef } from 'react';
import PhoneInput from 'react-phone-input-2';
import { requestCompanySignup, searchCpFirms } from '../../services/private/site-visit.service';
import Banner from './Banner';
import { ReactComponent as LeftArrowIcon } from '../../assets/plus_icon/leftArrow.svg';
import { ReactComponent as RefreshIcon } from '../../assets/plus_icon/refresh.svg';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { resolveSourcingManagerOption, isSourcingManagerValid } from './sourcingManagerUtils';

const customStyles = {
    control: (base, state) => ({
        ...base,
        boxShadow: "none",
        borderColor: '#ced4da',
        minHeight: '44px',
        height: '44px',
        '@media (min-width: 640px)': {
            minHeight: '54px',
            height: '54px',
        },
        minWidth: '100%',
        borderRadius: '12px',
        fontSize: '16px',
        fontWeight: '400',
        cursor: 'pointer',
        '&:focus': { borderColor: '#0062ff' },
    }),
    option: (styles, state) => ({
        ...styles,
        fontSize: '14px',
        cursor: 'pointer',
        borderBottom: '1px solid #E5E7EB',
        backgroundColor: state.isSelected ? "#f1f1f5" : styles.color,
        color: '#44444f',
        zIndex: 99,
        "&:hover": {
            color: "#44444f",
            backgroundColor: "#f1f1f5"
        },
        "&:last-child": {
            borderBottom: "none"
        }
    }),
};

const BrokerRegistration = ({
    setChannelPartner,
    isWithCP,
    isCPVisit,
    ownerName,
    setOwnerName,
    ownerMobile,
    setOwnerMobile,
    email,
    setEmail,
    name,
    setName,
    mobile,
    setMobile,
    companyName,
    setCompanyName,
    setCompanyId,
    rera,
    setRera,
    coverImage,
    projectName,
    cpContactUuid,
    setPreviousStepBeforeCapture,
    sourcingManager,
    setSourcingManager,
    setSourcingManagerSource,
    allSourcingManagers,
    fetchSourcingManagers,
    isSourcingRefreshing,
    isRevisit
}) => {
    const [error, setError] = useState(false);
    const [ownerMobileError, setOwnerMobileError] = useState(false);
    const [mobileError, setMobileError] = useState(false);
    const [companyError, setCompanyError] = useState('');
    const [designation, setDesignation] = useState('employee');
    const [emailError, setEmailError] = useState('');
    const [reraDuplicateError, setReraDuplicateError] = useState('');
    const [showReraModal, setShowReraModal] = useState(false);
    const [reraModalFirmName, setReraModalFirmName] = useState('');
    const reraInputRef = useRef(null);
    const [duplicateFirm, setDuplicateFirm] = useState(null);
    const [showDuplicateFirmModal, setShowDuplicateFirmModal] = useState(false);

    // Only initialize default designation on initial mount, do not auto-switch on ownerName typing
    useEffect(() => {
        if (!name && ownerName && !cpContactUuid) {
            setDesignation('owner');
        }
        // eslint-disable-next-hooks-deps
    }, []);

    const reraTimerRef = useRef(null);

    const checkReraDuplicate = (val) => {
        if (reraTimerRef.current) {
            clearTimeout(reraTimerRef.current);
        }

        if (!val || val.trim().length < 3) {
            setReraDuplicateError('');
            setReraModalFirmName('');
            return;
        }

        reraTimerRef.current = setTimeout(async () => {
            try {
                const res = await searchCpFirms(val.trim());
                if (res?.data?.status === 200) {
                    const searchResults = res.data?.data?.results || [];
                    const dup = searchResults.find(f => {
                        if (f.type === 'contact') return false;
                        const reraVal = f.rera_number || f.rera;
                        return reraVal && reraVal.toString().trim().toLowerCase() === val.trim().toLowerCase();
                    });

                    if (dup) {
                        const dupName = dup.name || dup.company_name || 'another firm';
                        setReraDuplicateError(`This RERA number is already registered to ${dupName}.`);
                        setReraModalFirmName(dupName);
                    } else {
                        setReraDuplicateError('');
                        setReraModalFirmName('');
                    }
                }
            } catch (err) {
                console.error('Error checking RERA duplicate:', err);
            }
        }, 300);
    };

    const handleReraBlur = () => {
        if (reraDuplicateError && reraModalFirmName) {
            setShowReraModal(true);
        }
    };

    const closeReraModal = () => {
        setShowReraModal(false);
        if (setRera) setRera('');
        setReraDuplicateError('');
        setReraModalFirmName('');
        if (reraInputRef.current) {
            reraInputRef.current.focus();
        }
    };

    const handleSubmit = () => {
        // 1. Validate Email Domain Restriction
        if (email && /@propacity\.(in|com)$/i.test(email.trim())) {
            setEmailError("Propacity email addresses aren't allowed for new CP users.");
            return;
        } else {
            setEmailError('');
        }

        // 2. Validate Duplicate RERA Check
        if (reraDuplicateError) {
            setShowReraModal(true);
            return;
        }

        // 3. Validate Basic Mandatory Required Fields
        if ((designation === 'employee' && (!name || !mobile)) || !ownerName || !ownerMobile || !companyName) {
            setError(true);
            return;
        } else {
            setError(false);
        }

        // 4. Validate Phone Number Lengths (Exact 10 digits)
        const cleanMobile = mobile ? mobile.replace(/\D/g, '') : '';
        const cleanOwnerMobile = ownerMobile ? ownerMobile.replace(/\D/g, '') : '';

        if (designation === 'employee' && cleanMobile.length < 10) {
            setMobileError(true);
            return;
        } else {
            setMobileError(false);
        }

        if (cleanOwnerMobile.length < 10) {
            setOwnerMobileError(true);
            return;
        } else {
            setOwnerMobileError(false);
        }

        // 5. Check if Company Name + Owner Mobile already exist in database (BEFORE RERA check so RERA can be pre-filled)
        // (Existing Firm Found popup is disabled as per requirement - proceed directly to RERA check)
        /*
        searchCpFirms(companyName.trim()).then(res => {
            if (res?.data?.status === 200) {
                const results = res.data?.data?.results || [];
                const matched = results.find(f => {
                    if (f.type === 'contact') return false;
                    const fName = (f.name || f.company_name || '').trim().toLowerCase();
                    const fMobile = (f.mobile || f.owner_mobile || '').toString().replace(/\D/g, '').slice(-10);
                    return fName === companyName.trim().toLowerCase() && fMobile === cleanOwnerMobile.slice(-10);
                });

                if (matched) {
                    setDuplicateFirm(matched);
                    setShowDuplicateFirmModal(true);
                    return;
                }
            }
            
            // If no existing firm matched, enforce mandatory RERA validation for new firm creation
            if (!rera || !rera.trim()) {
                setError(true);
                return;
            }
            proceedSubmission();
        }).catch(err => {
            console.error('Error checking duplicate company/owner mobile:', err);
            if (!rera || !rera.trim()) {
                setError(true);
                return;
            }
            proceedSubmission();
        });
        */

        // Enforce mandatory RERA validation for firm creation
        if (!rera || !rera.trim()) {
            setError(true);
            return;
        }
        proceedSubmission();
    };

    const proceedSubmission = () => {
        // If toggle is set to Owner, sync Owner values into Executive / Visitor fields
        if (designation === 'owner') {
            if (setName) setName(ownerName);
            if (setMobile) setMobile(ownerMobile);
        } else {
            if (setName) setName(name);
            if (setMobile) setMobile(mobile);
        }
        if (setPreviousStepBeforeCapture) {
            setPreviousStepBeforeCapture('8');
        }
        setChannelPartner('5');
    };

    return (
        <div className='mx-auto overflow-auto h-100'>
            <div className='!w-full broker-info'>
                <Banner coverImage={coverImage} projectName={projectName} />
            </div>

            {/* Single Merged Card matching Screenshot */}
            <div className='bg-white rounded-xl !my-3 sm:!my-6 !p-4 md:!p-12 space-y-4 md:space-y-6 animate-in fade-in-90 slide-in-from-top-2 duration-300 ease-out transition-all origin-top'>
                {/* 1. Back button */}
                <div className='!mb-2'>
                    <button
                        type='button'
                        className='flex items-center text-gray-700 hover:text-black font-medium text-sm sm:text-base cursor-pointer'
                        onClick={() => {
                            setName('');
                            setCompanyName('');
                            setMobile('');
                            setOwnerName('');
                            setOwnerMobile('');
                            setEmail('');
                            setRera('');
                            if (setCompanyId) setCompanyId('');
                            setChannelPartner('4');
                        }}
                    >
                        <LeftArrowIcon className='stroke-black800 rotate-180 mr-1.5 w-4 h-4 sm:w-5 sm:h-5' />
                        <span>Back</span>
                    </button>
                </div>

                {/* 2. Title, Designation & Status Badge Row */}
                <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-3 !mb-4'>
                    <h2 className='text-xl sm:text-2xl font-bold text-gray-900 mb-0'>
                        Enter company details
                    </h2>

                    {/* Right side: Unverified Badge + Designation Toggle (on extreme right) */}
                    <div className='flex items-center gap-3'>
                        <span className='bg-amber-50 text-amber-700 border border-amber-200 text-xs font-semibold px-2.5 py-1 rounded-md whitespace-nowrap'>
                            Unverified — pending review
                        </span>
                        <div className='inline-flex bg-gray-100/90 p-1 rounded-xl border border-gray-200'>
                            <button
                                type='button'
                                className={`px-3 py-1 text-xs sm:text-sm font-semibold rounded-lg transition-all ${
                                    designation === 'employee'
                                        ? 'bg-white text-[#0062FF] shadow-xs'
                                        : 'text-gray-500 hover:text-gray-800'
                                }`}
                                onClick={() => {
                                    setDesignation('employee');
                                    // When switching back to Employee, reset owner fields
                                    if (setOwnerName) setOwnerName('');
                                    if (setOwnerMobile) setOwnerMobile('91');
                                }}
                            >
                                Employee
                            </button>
                            <button
                                type='button'
                                className={`px-3 py-1 text-xs sm:text-sm font-semibold rounded-lg transition-all ${
                                    designation === 'owner'
                                        ? 'bg-white text-[#0062FF] shadow-xs'
                                        : 'text-gray-500 hover:text-gray-800'
                                }`}
                                onClick={() => {
                                    setDesignation('owner');
                                }}
                            >
                                Owner
                            </button>
                        </div>
                    </div>
                </div>
                {/* 1. CP Company Name */}
                <div className='flex flex-col font-medium relative w-full'>
                    <label className='sm:!text-lg text-base !mb-2'>
                        CP Company Name <sup className='text-red-500'>*</sup>
                    </label>
                    <input
                        className={`border rounded-xl focus:outline-none focus:!border-primary text-input sm:!text-lg text-sm !py-2 !px-3 sm:!py-3 sm:!px-4 font-normal text-gray-800 ${
                            error && !companyName ? 'border-red-500' : 'border-gray-300'
                        }`}
                        type='text'
                        placeholder='Enter CP Company Name'
                        name='company'
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                    />
                    {error && !companyName && (
                        <div className='text-red sm:!text-sm text-xs font-medium !mt-1.5'>
                            Please Enter CP Company Name
                        </div>
                    )}
                    {companyError && (
                        <div className='text-red sm:!text-sm text-xs font-medium !mt-1.5'>
                            {companyError}
                        </div>
                    )}
                </div>

                {/* 2. Company RERA Number */}
                <div className='flex flex-col font-medium relative w-full'>
                    <label className='sm:!text-lg text-base !mb-2'>
                        Company RERA Number <sup className='text-red-500'>*</sup>
                    </label>
                    <input
                        ref={reraInputRef}
                        className={`border rounded-xl focus:outline-none focus:!border-primary text-input sm:!text-lg text-sm !py-2 !px-3 sm:!py-3 sm:!px-4 font-normal text-gray-800 ${
                            (error && !rera) || reraDuplicateError ? 'border-red-500' : 'border-gray-300'
                        }`}
                        type='text'
                        placeholder='Enter Company RERA Number'
                        name='rera'
                        value={rera || ''}
                        onChange={(e) => {
                            const val = e.target.value;
                            setRera && setRera(val);
                            checkReraDuplicate(val);
                        }}
                        onBlur={handleReraBlur}
                    />
                    {error && !rera && (
                        <div className='text-red sm:!text-sm text-xs font-medium !mt-1.5'>
                            Please Enter Company RERA Number
                        </div>
                    )}
                    {reraDuplicateError && (
                        <div className='text-red sm:!text-sm text-xs font-medium !mt-1.5'>
                            {reraDuplicateError}
                        </div>
                    )}
                </div>

                {/* 3. Owner Name & Owner Mobile No. Row */}
                <div className='w-full flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6'>
                    <div className='flex flex-col font-medium relative w-full sm:w-1/2'>
                        <label className='sm:!text-lg text-base !mb-2'>
                            Owner Name <sup className='text-red-500'>*</sup>
                        </label>
                        <input
                            className={`border rounded-xl focus:outline-none focus:!border-primary text-input sm:!text-lg text-sm !py-2 !px-3 sm:!py-3 sm:!px-4 font-normal text-gray-800 ${
                                error && !ownerName ? 'border-red-500' : 'border-gray-300'
                            }`}
                            type='text'
                            placeholder={designation === 'owner' ? 'Enter Your Name' : 'Enter Owner Name'}
                            name='ownerName'
                            value={ownerName}
                            onChange={(e) => setOwnerName(e.target.value)}
                        />
                        {error && !ownerName && (
                            <div className='text-red sm:!text-sm text-xs font-medium !mt-1.5'>
                                Please Enter Owner Name
                            </div>
                        )}
                    </div>

                    <div className='flex flex-col font-medium relative w-full sm:w-1/2'>
                        <label className='sm:!text-lg text-base !mb-2'>
                            Owner Mobile No. <sup className='text-red-500'>*</sup>
                        </label>
                        <PhoneInput
                            inputClass={'border !w-full focus:outline-none focus:!border-primary rounded-xl text-input !h-[38px] sm:!h-[54px] sm:!text-lg text-sm !py-2 sm:!py-3'}
                            country={'in'}
                            value={ownerMobile}
                            countryCodeEditable={false}
                            enableSearch={true}
                            disableSearchIcon={true}
                            onChange={(phone) => setOwnerMobile(phone)}
                        />
                        {error && !ownerMobile && (
                            <div className='text-red sm:!text-sm text-xs font-medium !mt-1.5'>
                                Please Enter Owner Mobile No.
                            </div>
                        )}
                        {ownerMobileError && ownerMobile && (
                            <div className='text-red sm:!text-sm text-xs font-medium !mt-1.5'>
                                Please Enter Valid Mobile No.
                            </div>
                        )}
                    </div>
                </div>

                {/* 4. CP Executive Name & Mobile No. Row (Conditional for Employee) */}
                {designation === 'employee' && (
                    <div className='w-full flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6'>
                        <div className='flex flex-col font-medium relative w-full sm:w-1/2'>
                            <label className='sm:!text-lg text-base !mb-2'>
                                CP Executive Name <sup className='text-red-500'>*</sup>
                            </label>
                            <input
                                className={`border rounded-xl focus:outline-none focus:!border-primary text-input sm:!text-lg text-sm !py-2 !px-3 sm:!py-3 sm:!px-4 font-normal text-gray-800 ${
                                    error && !name ? 'border-red-500' : 'border-gray-300'
                                }`}
                                type='text'
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder='Enter Your Name'
                            />
                            {error && !name && (
                                <div className='text-red sm:!text-sm text-xs font-medium !mt-1.5'>
                                    Please Enter CP Executive Name
                                </div>
                            )}
                        </div>

                        <div className='flex flex-col font-medium relative w-full sm:w-1/2'>
                            <label className='sm:!text-lg text-base !mb-2'>
                                Executive Mobile No. <sup className='text-red-500'>*</sup>
                            </label>
                            <PhoneInput
                                inputClass={'border !w-full focus:outline-none focus:!border-primary rounded-xl text-input !h-[38px] sm:!h-[54px] sm:!text-lg text-sm !py-2 sm:!py-3'}
                                country={'in'}
                                value={mobile}
                                countryCodeEditable={false}
                                enableSearch={true}
                                disableSearchIcon={true}
                                onChange={(phone) => setMobile(phone)}
                            />
                            {error && !mobile && (
                                <div className='text-red sm:!text-sm text-xs font-medium !mt-1.5'>
                                    Please Enter Mobile No.
                                </div>
                            )}
                            {mobileError && mobile && (
                                <div className='text-red sm:!text-sm text-xs font-medium !mt-1.5'>
                                    Please Enter Valid Mobile No.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* 5. Email (Optional) and Sourcing Manager Row */}
                <div className='w-full flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6'>
                    <div className='flex flex-col font-medium relative w-full sm:w-1/2'>
                        <label className='sm:!text-lg text-base !mb-2'>
                            Email <span className='text-gray-400 font-normal text-sm'>(Optional)</span>
                        </label>
                        <input
                            className={`border rounded-xl focus:outline-none focus:!border-primary text-input sm:!text-lg text-sm !py-2 !px-3 sm:!py-3 sm:!px-4 font-normal text-gray-800 ${
                                emailError ? 'border-red-500' : 'border-gray-300'
                            }`}
                            type='email'
                            placeholder='Enter Email'
                            name='email'
                            value={email || ''}
                            onChange={(e) => {
                                const val = e.target.value;
                                setEmail(val);
                                if (/@propacity\.(in|com)$/i.test(val.trim())) {
                                    setEmailError("Propacity email addresses aren't allowed for new CP users.");
                                } else {
                                    setEmailError('');
                                }
                            }}
                        />
                        {emailError && (
                            <div className='text-red sm:!text-sm text-xs font-medium !mt-1.5'>
                                {emailError}
                            </div>
                        )}
                    </div>

                    <div className='flex flex-col font-medium relative w-full sm:w-1/2'>
                        <label className='sm:!text-lg text-base !mb-2'>Sourcing Manager</label>
                        <div className="flex items-center gap-2 sm:gap-3 w-full">
                            <div className="flex-1 min-w-0">
                                <Select
                                    options={allSourcingManagers}
                                    placeholder={'Select Sourcing Manager'}
                                    styles={customStyles}
                                    value={sourcingManager}
                                    onChange={(value) => {
                                        setSourcingManager && setSourcingManager(value ? resolveSourcingManagerOption(value, allSourcingManagers) : null);
                                        setSourcingManagerSource && setSourcingManagerSource('manual');
                                    }}
                                    isClearable={true}
                                    noOptionsMessage={() => "No Sourcing Managers found"}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={fetchSourcingManagers}
                                disabled={isSourcingRefreshing}
                                className="h-[44px] w-[44px] sm:h-[54px] sm:w-[54px] flex items-center justify-center rounded-xl border border-gray-300 bg-white hover:bg-gray-50 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex-shrink-0"
                                title="Refresh sourcing managers list"
                            >
                                <RefreshIcon className={`w-5 h-5 text-gray-500 ${isSourcingRefreshing ? 'spin-animation' : ''}`} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Footer Action Buttons */}
                <div className='w-full sm:space-x-4 space-y-4 space-y-reverse sm:space-y-0 flex flex-col-reverse sm:flex-row !mt-6'>
                    <button
                        type='button'
                        className='w-full sm:w-1/2 text-base sm:!text-lg border rounded-xl !py-2 sm:!py-3.5 hover:bg-gray-50 transition-colors'
                        onClick={() => {
                            setName('');
                            setCompanyName('');
                            setMobile('');
                            setOwnerName('');
                            setOwnerMobile('');
                            setEmail('');
                            setRera('');
                            if (setCompanyId) setCompanyId('');
                            setChannelPartner('4');
                        }}
                    >
                        &larr; Back
                    </button>
                    <button
                        type='button'
                        className='w-full sm:w-1/2 text-base sm:!text-lg rounded-xl !py-2 sm:!py-3.5 bg-[#0062FF] hover:bg-[#2064FA] text-white font-medium transition-colors'
                        onClick={handleSubmit}
                    >
                        Next
                    </button>
                </div>
            </div>

            {/* RERA Duplicate Warning Modal matching tablet_form_cp_flow_mockup_2.html */}
            {showReraModal && (
                <div className='fixed inset-0 bg-[#101828]/45 flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-150'>
                    <div className='bg-white border border-gray-200 border-t-[3px] border-t-[#FC5A5A] rounded-[10px] shadow-lg max-w-[400px] w-full p-6 text-gray-800 animate-in zoom-in-95 duration-150'>
                        <div className='flex items-center gap-3 mb-3'>
                            <div className='w-9 h-9 rounded-full bg-[#FC5A5A]/10 text-[#FC5A5A] flex items-center justify-center text-lg font-bold flex-shrink-0'>
                                &#9888;
                            </div>
                            <h3 className='text-base font-semibold text-[#FC5A5A] mb-0'>
                                RERA number already registered
                            </h3>
                        </div>
                        <p className='text-sm text-[#667085] mb-5 leading-[20px]'>
                            <strong className='font-semibold text-[#344054]'>{rera}</strong> is already registered to{' '}
                            <strong className='font-semibold text-[#344054]'>{reraModalFirmName || 'another firm'}</strong>. Each company must have a unique RERA number — please check the number and enter the correct one.
                        </p>
                        <div className='w-full'>
                            <button
                                type='button'
                                className='w-full bg-[#FC5A5A] hover:bg-[#e04848] text-white font-semibold px-5 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors cursor-pointer shadow-xs text-center'
                                onClick={closeReraModal}
                            >
                                Change RERA Number
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Existing Company Found Warning & Selection Modal (Disabled/Commented as per requirement) */}
            {/*
            {showDuplicateFirmModal && duplicateFirm && (
                <div className='fixed inset-0 bg-[#101828]/45 flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-150'>
                    <div className='bg-white border border-gray-200 border-t-[3px] border-t-amber-500 rounded-[12px] shadow-xl max-w-[420px] w-full p-6 text-gray-800 animate-in zoom-in-95 duration-150'>
                        <div className='flex items-center gap-3 mb-3 pb-3 border-b border-gray-100'>
                            <div className='w-9 h-9 rounded-full bg-[#FEF0C7] text-[#B54708] flex items-center justify-center text-lg font-bold flex-shrink-0 border border-[#FDB022]'>
                                &#9888;
                            </div>
                            <h3 className='text-base sm:text-lg font-semibold text-gray-900 mb-0'>
                                Existing Firm Found
                            </h3>
                        </div>
                        <p className='text-sm text-gray-600 mb-4 leading-[22px]'>
                            A company with the name <strong className='font-semibold text-gray-900'>{duplicateFirm.name || duplicateFirm.company_name}</strong> and owner mobile <strong className='font-semibold text-gray-900'>{ownerMobile}</strong> already exists in our records.
                        </p>
                        <div className='bg-blue-50/70 border border-blue-100 rounded-lg p-3 mb-5 text-xs text-blue-900 flex justify-between items-center'>
                            <div>
                                <span className='font-bold block text-sm text-blue-950'>{duplicateFirm.name || duplicateFirm.company_name}</span>
                                {duplicateFirm.rera_number || duplicateFirm.rera ? (
                                    <span className='text-blue-700'>RERA: {duplicateFirm.rera_number || duplicateFirm.rera}</span>
                                ) : (
                                    <span className='text-gray-500 italic'>No RERA on file</span>
                                )}
                            </div>
                            <span className='w-5 h-5 rounded-full bg-[#01AC47] text-white flex items-center justify-center text-xs font-bold flex-shrink-0'>✓</span>
                        </div>
                        <div className='flex items-center justify-end gap-3'>
                            <button
                                type='button'
                                className='px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer'
                                onClick={() => setShowDuplicateFirmModal(false)}
                            >
                                Edit Details
                            </button>
                            <button
                                type='button'
                                className='bg-[#0062FF] hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg text-sm transition-colors cursor-pointer shadow-xs'
                                onClick={() => {
                                    setShowDuplicateFirmModal(false);
                                    const formattedOption = {
                                        label: duplicateFirm.name || duplicateFirm.company_name,
                                        value: duplicateFirm.id || duplicateFirm.uuid,
                                        data: duplicateFirm
                                    };
                                    if (setCompanyId) setCompanyId(formattedOption);
                                    const firmRera = duplicateFirm.rera_number || duplicateFirm.rera || '';
                                    if (setRera) setRera(firmRera);
                                    toast.success(`Selected existing firm ${duplicateFirm.name || duplicateFirm.company_name}`, { toastId: 'dup-firm-select' });
                                    setChannelPartner('4');
                                }}
                            >
                                Select Existing Firm
                            </button>
                        </div>
                    </div>
                </div>
            )}
            */}
        </div>
    );
};

export default BrokerRegistration;